import { createApp } from "vue";
import { createPinia } from "pinia";
import { setPiniaInstance } from "@smeetz/order-store";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import i18n from "@/lang";
import {
  appSetup,
  customerDisplaySetup,
  isCustomerDisplayWindow,
} from "./utils/setup";
import * as Sentry from "@sentry/vue";
import mitt from "mitt";

import { setupPermissionGuard } from "@/permission";
import { setupPosClientGroupSync } from "@/utils/pos-client-init";
import { setupFeatureFlags } from "@/utils/helpers/feature-flags";
import { initEndpointResolver } from "@/config/endpoint-resolver";
import { flagsmithEndpointResolver } from "@/config/flagsmith-endpoint-resolver";
import { initSymphonyApi } from "@smeetz/services-api";

/* Element Plus */
// import "@/styles/element/index.scss";

// import ElementPlus from "element-plus";
// import all element css, uncommented next line
// import "element-plus/dist/index.css";

/* Tailwind + @smeetz/ui-core-pos theme (must load before app styles so Bulma/maz overrides win) */

/* Smeetz style */
import "@/styles/index.scss";

import "@/styles/tailwind.css";

import "@/styles/smeetz-ui-overrides.scss";
import setupDayjs from "./utils/timezone";

setupDayjs();

const emitter = mitt();
const pinia = createPinia();
setPiniaInstance(pinia);
const app = createApp(App);

// Disable DevTools production environment only.
// Local dev and the deployed dev/test environments keep devtools
// available. Gate on VITE_ENV (not import.meta.env.PROD).
if (import.meta.env.VITE_ENV === "prod") {
  // @ts-ignore
  app.config.devtools = false;
  app.config.performance = false;
  // @ts-ignore
  app.config.globalProperties.__VUE_DEVTOOLS__ = false;
  // @ts-ignore
  app.config.silent = true;
}

Sentry.init({
  app,
  environment: import.meta.env.VITE_ENV,
  release: import.meta.env.VITE_APP_VERSION,
  dsn: "https://8aa883537fa245a28a9c8fecfcf479c7@o1255856.ingest.sentry.io/6589223",
  integrations: [
    Sentry.browserTracingIntegration({
      router,
    }),
  ],
  tracePropagationTargets: [
    "localhost",
    "dev-pos.smeetz.com",
    "test-pos.smeetz.com",
    "pos.smeetz.com",
    /^\//,
  ],
  // debug: process.env.VUE_APP_BUILD_ENV !== 'prod',
  debug: false,
  tracesSampleRate: 1.0,
  tracingOptions: {
    trackComponents: true,
  },
  // Vue specific
  logErrors: true,
  attachProps: true,
  attachStacktrace: true,
  beforeSend(event) {
    // Check if the error originates from chart.js of seats io
    // I noticed whenever we select/deselect seats it throws errors althouth the seat is hold (error: object not valid json object)
    // lets ignore it
    if (
      event.exception &&
      event.exception.values &&
      event.exception.values.length
    ) {
      const exception = event.exception.values[0];
      if (exception && exception.stacktrace && exception.stacktrace.frames) {
        const frames = exception.stacktrace.frames;
        for (let i = 0; i < frames.length; i++) {
          if (frames[i].filename && frames[i].filename?.includes("chart.js")) {
            // Ignore the error
            return null;
          }
        }
      }
    }
    // Otherwise, send the event to Sentry
    return event;
  },
});

// app.use(ElementPlus);
app.config.globalProperties.emitter = emitter;
// Route endpoint reads through the Flagsmith per-group override.
initEndpointResolver(flagsmithEndpointResolver);
app.use(i18n).use(store).use(pinia).use(router);
setupPermissionGuard();
setupPosClientGroupSync();
setupFeatureFlags();

if (isCustomerDisplayWindow()) {
  customerDisplaySetup();
} else {
  appSetup();
}

// Symphony API powers the country list behind design-system components like
// SCountrySelect. Gate on VITE_ENV, defaulting to prod for any other value.
initSymphonyApi(
  import.meta.env.VITE_ENV === "dev" || import.meta.env.VITE_ENV === "test"
    ? import.meta.env.VITE_ENV
    : "prod"
);

app.mount("#app");
